/**
 * Keycloak authentication via the standard redirect (Authorization Code + PKCE) flow.
 *
 * Sign-in, password resets, OTP/MFA and required actions all happen on the
 * Keycloak-hosted pages, so realm policies apply. The app never sees credentials.
 * User registration remains app-driven (admin creates users; backend provisions
 * the Keycloak account and sends the setup email).
 */
import Keycloak from 'keycloak-js';

export interface UserInfo {
  sub: string;
  email?: string;
  preferred_username?: string;
  given_name?: string;
  family_name?: string;
  name?: string;
}

class KeycloakAuth {
  private keycloak: Keycloak;
  private initPromise: Promise<boolean> | null = null;

  constructor() {
    this.keycloak = new Keycloak({
      url: process.env.REACT_APP_KEYCLOAK_URL || 'http://localhost:8080',
      realm: process.env.REACT_APP_KEYCLOAK_REALM || 'voucher-psp',
      clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'admin-portal',
    });
  }

  /**
   * Initialize the adapter once. login-required sends unauthenticated visitors
   * straight to the Keycloak-hosted login page (there is no app login page);
   * the current URL is preserved so deep links survive the round-trip.
   */
  init(): Promise<boolean> {
    if (!this.initPromise) {
      this.keycloak.onTokenExpired = () => {
        this.keycloak.updateToken(30).catch(() => this.login());
      };
      this.initPromise = this.keycloak
        .init({
          onLoad: 'login-required',
          pkceMethod: 'S256',
          checkLoginIframe: false,
        })
        .catch((error) => {
          console.error('Keycloak init failed:', error);
          return false;
        });
    }
    return this.initPromise;
  }

  /** Redirect to the Keycloak-hosted login page. */
  login(redirectUri?: string): void {
    this.keycloak.login({
      redirectUri: redirectUri || window.location.href,
    });
  }

  /**
   * End the Keycloak SSO session. Landing back on the app root immediately
   * bounces to the Keycloak login page (login-required).
   */
  logout(): void {
    this.keycloak.logout({ redirectUri: window.location.origin });
  }

  isAuthenticated(): boolean {
    return !!this.keycloak.authenticated && !!this.keycloak.token;
  }

  getToken(): string | null {
    return this.keycloak.token ?? null;
  }

  async getUserInfo(): Promise<UserInfo | null> {
    if (!this.isAuthenticated()) return null;
    const claims = this.keycloak.tokenParsed as Record<string, any> | undefined;
    if (!claims) return null;
    return {
      sub: claims.sub,
      email: claims.email,
      preferred_username: claims.preferred_username,
      given_name: claims.given_name,
      family_name: claims.family_name,
      name: claims.name,
    };
  }

  getRoles(): string[] {
    const claims = this.keycloak.tokenParsed as Record<string, any> | undefined;
    const realmRoles: string[] = claims?.realm_access?.roles || [];
    const clientRoles: string[] =
      claims?.resource_access?.[this.keycloak.clientId!]?.roles || [];
    return [...realmRoles, ...clientRoles];
  }

  hasRole(role: string): boolean {
    return this.getRoles().includes(role);
  }

  /** Refresh the token if it expires within 30s. Resolves false when refresh fails. */
  async refreshToken(): Promise<boolean> {
    try {
      await this.keycloak.updateToken(30);
      return true;
    } catch {
      return false;
    }
  }
}

export const keycloakAuth = new KeycloakAuth();
export default keycloakAuth;
